import { ZodError } from "zod";
import { narrativeWithDataValuesSchema } from "./index.js";
import {
  KNOWN_ITEM_TYPES,
  KNOWN_ANIMATION_STYLES,
  KNOWN_TEXT_STYLES,
} from "./item.schema.js";

/**
 * Validate a narrative object against the schema
 * @param data - The narrative data to validate
 * @returns Safe parse result with success flag and either data or error
 */
export function validateNarrative(data: unknown) {
  return narrativeWithDataValuesSchema.safeParse(data);
}

/**
 * Format Zod validation errors into a human-readable string
 * @param error - The ZodError from validation
 * @returns Formatted error message with paths and descriptions
 */
export function formatValidationErrors(error: ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join(".") : "root";
      return `  • ${path}: ${issue.message}`;
    })
    .join("\n");
}

/**
 * Validate and throw if invalid
 * Useful when you want validation errors to be fatal
 * @param data - The narrative data to validate
 * @returns The validated and typed narrative data
 * @throws {Error} If validation fails
 */
export function validateNarrativeOrThrow(data: unknown) {
  const result = validateNarrative(data);
  if (!result.success) {
    throw new Error(
      `Narrative validation failed:\n${formatValidationErrors(result.error)}`,
    );
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Lenient validation — sanitize unrecognized config before validating
// ---------------------------------------------------------------------------

interface SanitizeResult {
  sanitized: unknown;
  warnings: string[];
}

/**
 * Pre-process a narrative JSON to strip unrecognized config that would cause
 * the strict Zod schema to reject the entire narrative.
 *
 * Handles:
 * - Unknown item `type` values (filtered out of groups)
 * - Empty groups after item filtering (removed from sections)
 * - Empty sections after group filtering (removed from narrative)
 * - Unknown `animationStyle` values (replaced with undefined)
 * - Unknown text `style` preset strings (replaced with "body")
 */
function sanitizeNarrative(data: unknown): SanitizeResult {
  const warnings: string[] = [];

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { sanitized: data, warnings };
  }

  const narrative = data as Record<string, unknown>;
  const sections = narrative.sections;

  if (!Array.isArray(sections)) {
    return { sanitized: data, warnings };
  }

  const sanitizedSections: unknown[] = [];

  for (let si = 0; si < sections.length; si++) {
    const section = sections[si];
    if (!section || typeof section !== "object" || Array.isArray(section)) {
      sanitizedSections.push(section);
      continue;
    }

    const sectionObj = section as Record<string, unknown>;
    const sectionPath = `sections[${si}]${sectionObj.id ? `(${sectionObj.id})` : ""}`;
    const groups = sectionObj.groups;

    if (!Array.isArray(groups)) {
      sanitizedSections.push(section);
      continue;
    }

    const sanitizedGroups: unknown[] = [];

    for (let gi = 0; gi < groups.length; gi++) {
      const group = groups[gi];
      if (!group || typeof group !== "object" || Array.isArray(group)) {
        sanitizedGroups.push(group);
        continue;
      }

      const groupObj = { ...(group as Record<string, unknown>) };
      const groupPath = `${sectionPath}.groups[${gi}]${groupObj.id ? `(${groupObj.id})` : ""}`;

      // Sanitize group-level animationStyle
      if (
        groupObj.animationStyle !== undefined &&
        !KNOWN_ANIMATION_STYLES.includes(groupObj.animationStyle as string)
      ) {
        warnings.push(
          `${groupPath}.animationStyle: unknown value "${groupObj.animationStyle}" removed`,
        );
        delete groupObj.animationStyle;
      }

      const items = groupObj.items;
      if (!Array.isArray(items)) {
        sanitizedGroups.push(groupObj);
        continue;
      }

      const sanitizedItems: unknown[] = [];

      for (let ii = 0; ii < items.length; ii++) {
        const item = items[ii];
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          sanitizedItems.push(item);
          continue;
        }

        const itemObj = item as Record<string, unknown>;
        const itemType = itemObj.type;

        // Filter out items whose type is missing, non-string, or not in the allowlist
        if (
          typeof itemType !== "string" ||
          !KNOWN_ITEM_TYPES.includes(itemType)
        ) {
          warnings.push(
            `${groupPath}.items[${ii}]: invalid item type ${JSON.stringify(itemType)} removed`,
          );
          continue;
        }

        const sanitizedItem = { ...itemObj };

        // Sanitize item-level animationStyle
        if (
          sanitizedItem.animationStyle !== undefined &&
          !KNOWN_ANIMATION_STYLES.includes(
            sanitizedItem.animationStyle as string,
          )
        ) {
          warnings.push(
            `${groupPath}.items[${ii}].animationStyle: unknown value "${sanitizedItem.animationStyle}" removed`,
          );
          delete sanitizedItem.animationStyle;
        }

        // Sanitize text style preset (when style is a plain string, not an object)
        if (
          itemType === "text" &&
          typeof sanitizedItem.style === "string" &&
          !KNOWN_TEXT_STYLES.includes(sanitizedItem.style)
        ) {
          warnings.push(
            `${groupPath}.items[${ii}].style: unknown preset "${sanitizedItem.style}" replaced with "body"`,
          );
          sanitizedItem.style = "body";
        }

        // Sanitize text style preset inside style object
        if (
          itemType === "text" &&
          sanitizedItem.style &&
          typeof sanitizedItem.style === "object" &&
          !Array.isArray(sanitizedItem.style)
        ) {
          const styleObj = sanitizedItem.style as Record<string, unknown>;
          if (
            styleObj.preset !== undefined &&
            typeof styleObj.preset === "string" &&
            !KNOWN_TEXT_STYLES.includes(styleObj.preset)
          ) {
            warnings.push(
              `${groupPath}.items[${ii}].style.preset: unknown preset "${styleObj.preset}" replaced with "body"`,
            );
            sanitizedItem.style = { ...styleObj, preset: "body" };
          }
          if (
            styleObj.animationStyle !== undefined &&
            !KNOWN_ANIMATION_STYLES.includes(styleObj.animationStyle as string)
          ) {
            warnings.push(
              `${groupPath}.items[${ii}].style.animationStyle: unknown value "${styleObj.animationStyle}" removed`,
            );
            sanitizedItem.style = {
              ...(sanitizedItem.style as Record<string, unknown>),
            };
            delete (sanitizedItem.style as Record<string, unknown>)
              .animationStyle;
          }
        }

        sanitizedItems.push(sanitizedItem);
      }

      if (sanitizedItems.length === 0) {
        warnings.push(
          `${groupPath}: removed (no valid items remaining after sanitization)`,
        );
        continue;
      }

      sanitizedGroups.push({ ...groupObj, items: sanitizedItems });
    }

    if (sanitizedGroups.length === 0) {
      warnings.push(
        `${sectionPath}: removed (no valid groups remaining after sanitization)`,
      );
      continue;
    }

    sanitizedSections.push({ ...sectionObj, groups: sanitizedGroups });
  }

  if (sanitizedSections.length === 0 && sections.length > 0) {
    warnings.push(
      "All sections removed during sanitization — narrative cannot be loaded",
    );
    return { sanitized: data, warnings };
  }

  return {
    sanitized: { ...narrative, sections: sanitizedSections },
    warnings,
  };
}

type NarrativeData = ReturnType<typeof narrativeWithDataValuesSchema.parse>;

export type LenientValidationResult =
  | { success: true; data: NarrativeData; warnings: string[] }
  | { success: false; error: ZodError; warnings: string[] };

/**
 * Validate a narrative with graceful degradation.
 *
 * Sanitizes the input first (strips unrecognized item types, unknown enum
 * values, etc.) then runs the normal Zod schema validation on the cleaned
 * data. Warnings are collected for every modification made during sanitization.
 *
 * Use this for browser-side loading where partial content is better than
 * no content. Pipeline/CLI paths should continue using validateNarrative().
 */
export function validateNarrativeLenient(
  data: unknown,
): LenientValidationResult {
  const { sanitized, warnings } = sanitizeNarrative(data);
  const result = narrativeWithDataValuesSchema.safeParse(sanitized);

  if (result.success) {
    return { success: true, data: result.data, warnings };
  }
  return { success: false, error: result.error, warnings };
}
